import React, { useState, useRef, useEffect } from "react";
import useGetHealthLogs from "../../hooks/useGetHealthLogs";
import { RefreshCw, X, Plus, Check, ChevronDown } from "lucide-react";

import DetailsTable from "../detailsView/DetailsTable.js";
import LoadingSpinner from "../components/LoadingSpinner";
import { cn } from "lib/utils";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Select } from "components/ui/select";
import { Badge } from "components/ui/badge";

function ErrorAlert({ error }) {
    return (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            <strong>Warning!</strong> {error}
        </div>
    );
}

function TimeOutAlert({ refetchQueryResults }) {
    return (
        <div className="flex items-center gap-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-warning">
            <span><strong>Time Out!</strong> Please refresh after some time</span>
            <Button size="icon-sm" onClick={refetchQueryResults} title="Refresh">
                <RefreshCw />
            </Button>
        </div>
    );
}

function KeyBadges({ extraQueryArg, removeExtraQueryArg }) {
    return (
        <div className="flex flex-wrap gap-2">
            {Object.keys(extraQueryArg).map((key) =>
                extraQueryArg[key] != "" ? (
                    <button
                        key={key}
                        onClick={() => removeExtraQueryArg(key)}
                        title="Remove filter"
                        className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                    >
                        <span>{key}: {extraQueryArg[key]}</span>
                        <X className="h-3.5 w-3.5" />
                    </button>
                ) : (
                    ""
                )
            )}
        </div>
    );
}

let search_key_list = ["statusCode", "remoteAddr", "upstreamAddr", "url"];
let allColumnKeyList = [
    "time",
    "statusCode",
    "url",
    "remoteAddr",
    "upstreamAddr",
    "userAgent",
    "requestTime",
    "upstreamTime",
    "dateTimeEpoch",
    "requestMethod",
    "requestProtocol",
    "bytes",
    "referrer",
    "serverName",
    "forwaredFor",
    "cacheStatus",
    "upstreacCacheControl",
    "upstreamExpires",
    "cdn",
];
let emptyDict = {};
search_key_list.forEach((key) => {
    emptyDict[key] = "";
});

// Row highlight color by HTTP status class: 5xx = error, 4xx = warning,
// everything else (2xx/3xx/…) = no highlight. Returns Tailwind classes or "".
export function statusRowClass(statusCode) {
    const code = String(statusCode ?? "");
    if (code.startsWith("5")) return "bg-destructive/10 text-destructive";
    if (code.startsWith("4")) return "bg-warning/10 text-warning";
    return "";
}

function RequestUrls({ eventId, environment, startTime, endTime, theme, logSource=null}) {
    const [queryId, setQueryId] = useState(null);
    const [extraQueryArg, setExtraQueryArg] = useState(emptyDict);
    const [extraQueryArgFlag, setExtraQueryArgFlag] = useState(0);
    const [selectedColumnKeyList, setSelectedColumnKeyList] = useState([
        "time",
        "statusCode",
        "url",
        "remoteAddr",
        "upstreamAddr",
        "userAgent",
        "requestTime",
        "upstreamTime",
    ]);
    const [isColumnMenuOpen, setIsColumnMenuOpen] = useState(false);
    const columnMenuRef = useRef(null);

    const { queryLogs, error, isLoaded, isTimeOut, refetchQueryResults } = useGetHealthLogs(
        "filter_url_by_multiple_fields",
        eventId,
        environment,
        startTime,
        endTime,
        setQueryId,
        extraQueryArg,
        logSource
    );

    const [selectedKey, setSelectedKey] = useState(search_key_list[0]);
    const [selectedValue, setSelectedValue] = useState("");

    function updateExtraQueryArg() {
        if (selectedKey && selectedValue) {
            extraQueryArg[selectedKey] = selectedValue;
            setSelectedKey(search_key_list[0]);
            setSelectedValue("");
            setExtraQueryArgFlag(Math.random());
        }
    }

    function removeExtraQueryArg(key) {
        if (key) {
            extraQueryArg[key] = "";
            setSelectedKey(search_key_list[0]);
            setSelectedValue("");
            setExtraQueryArgFlag(Math.random());
        }
    }

    function getStatusCodesNot200() {
        const statusCodesCount = {};
        if (isLoaded && queryLogs.log_data.length > 0) {
            for (const item of queryLogs.log_data) {
                if (item.statusCode !== "200") {
                    if (!statusCodesCount[item.statusCode]) {
                        statusCodesCount[item.statusCode] = 1;
                    } else {
                        statusCodesCount[item.statusCode]++;
                    }
                }
            }
        }
        return statusCodesCount;
    }

    const handleSelectColumn = (option) => {
        if (selectedColumnKeyList.includes(option)) {
            setSelectedColumnKeyList(selectedColumnKeyList.filter((item) => item !== option));
        } else {
            setSelectedColumnKeyList([...selectedColumnKeyList, option]);
        }
    };

    const selectAllColumns = () => setSelectedColumnKeyList([...allColumnKeyList]);
    const clearAllColumns = () => setSelectedColumnKeyList([]);

    useEffect(() => {
        if (!isColumnMenuOpen) return;
        const handleClickOutside = (event) => {
            if (columnMenuRef.current && !columnMenuRef.current.contains(event.target)) {
                setIsColumnMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isColumnMenuOpen]);

    let cardBodyContent;
    if (isLoaded) {
        cardBodyContent = (
            <div>
                <DetailsTable
                    logData={queryLogs.log_data}
                    keyList={allColumnKeyList.filter((item) =>
                        selectedColumnKeyList.includes(item)
                    )}
                    theme={theme}
                    rowHighlightClass={(row) => statusRowClass(row.statusCode)}
                />
            </div>
        );
    } else if (error) {
        cardBodyContent = <ErrorAlert error={error} />;
    } else if (isTimeOut) {
        cardBodyContent = <TimeOutAlert refetchQueryResults={refetchQueryResults} />;
    } else {
        cardBodyContent = <LoadingSpinner />;
    }

    // Tally non-200 status codes for the summary badges below.
    const statusCodesNot200 = getStatusCodesNot200();
    const hasActiveFilters = Object.keys(extraQueryArg).some((key) => extraQueryArg[key] !== "");
    const hasErrors = Object.keys(statusCodesNot200).length > 0;

    return (
        <div className="pb-3">
            <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                    <Select
                        id="keyList"
                        value={selectedKey}
                        onChange={(e) => setSelectedKey(e.target.value)}
                        className="w-auto"
                    >
                        {search_key_list.map((item, index) => (
                            <option value={item} key={index}>
                                {item}
                            </option>
                        ))}
                    </Select>
                    <Input
                        type="text"
                        id="value"
                        name="value"
                        value={selectedValue}
                        onChange={(e) => setSelectedValue(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && updateExtraQueryArg()}
                        placeholder="Value"
                        className="w-auto"
                    />
                    <Button
                        size="icon"
                        onClick={updateExtraQueryArg}
                        disabled={!selectedValue}
                        title="Add filter"
                    >
                        <Plus />
                    </Button>
                    <div className="ml-auto flex items-center gap-2">
                        <Button onClick={refetchQueryResults}>Filter urls</Button>
                        {getColumnDropDown()}
                    </div>
                </div>
                {(hasActiveFilters || hasErrors) && (
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <KeyBadges
                            extraQueryArg={extraQueryArg}
                            removeExtraQueryArg={removeExtraQueryArg}
                        />
                        <div className="flex flex-wrap items-center gap-2">
                            {Object.entries(statusCodesNot200).map(
                                ([statusCode, count], index) => (
                                    <Badge key={index} variant="destructive" size="sm">
                                        status {statusCode}: {count}
                                    </Badge>
                                )
                            )}
                        </div>
                    </div>
                )}
            </div>

            {cardBodyContent}
        </div>
    );

    function getColumnDropDown() {
        let optionsPerRow = 4;
        return (
            <div className="relative inline-block" ref={columnMenuRef}>
                <Button
                    variant="outline"
                    title="Select columns"
                    onClick={() => setIsColumnMenuOpen((open) => !open)}
                >
                    Select columns
                    <ChevronDown
                        className={cn("transition-transform", isColumnMenuOpen && "rotate-180")}
                    />
                </Button>
                {isColumnMenuOpen && (
                    <div className="absolute right-0 z-10 mt-1 rounded-md border border-border bg-popover p-1 shadow-lg">
                        <div className="flex items-center justify-between gap-6 px-3 py-1.5">
                            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                Columns
                            </span>
                            <div className="flex items-center gap-2 text-xs">
                                <button
                                    type="button"
                                    onClick={selectAllColumns}
                                    className="font-medium text-primary transition-colors hover:underline"
                                >
                                    Select all
                                </button>
                                <span className="text-border">|</span>
                                <button
                                    type="button"
                                    onClick={clearAllColumns}
                                    className="font-medium text-primary transition-colors hover:underline"
                                >
                                    Clear
                                </button>
                            </div>
                        </div>
                        <div className="mt-1 border-t border-border pt-1">
                            {(() => {
                                const elements = [];
                                for (let i = 0; i < allColumnKeyList.length; i += optionsPerRow) {
                                    const options = allColumnKeyList.slice(i, i + optionsPerRow);
                                    const row = (
                                        <div className="flex" key={i}>
                                            {options.map((option, index) => {
                                                const checked = selectedColumnKeyList.includes(option);
                                                return (
                                                    <button
                                                        key={index}
                                                        type="button"
                                                        title={option}
                                                        className="flex w-[170px] items-center gap-2 rounded px-3 py-2 text-left text-sm transition-colors hover:bg-accent hover:text-accent-foreground"
                                                        onClick={() => handleSelectColumn(option)}
                                                    >
                                                        <span
                                                            className={cn(
                                                                "flex h-4 w-4 shrink-0 items-center justify-center rounded border",
                                                                checked
                                                                    ? "border-primary bg-primary text-primary-foreground"
                                                                    : "border-input"
                                                            )}
                                                        >
                                                            {checked && <Check className="h-3 w-3" />}
                                                        </span>
                                                        <span className="truncate">{option}</span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    );
                                    elements.push(row);
                                }
                                return elements;
                            })()}
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

export default RequestUrls;
